/**
 * O'Copro - Email Service
 * Envoi d'emails via Firebase Extension "Trigger Email from Firestore"
 * 
 * Fonctionnement :
 * - On crée un document dans la collection `mail`
 * - L'extension Firebase détecte le document et envoie l'email via SMTP
 * 
 * ✅ Inclut :
 * - Référence copropriété auprès du syndic (syndicReference)
 * - Identifiant O'Copro (residenceCode)
 * - Identifiant locataire/copropriétaire (submitterIdentifier)
 * - Pièce jointe photo (photoURL)
 * - ✅ NOUVEAU: Pièce jointe PDF étiquette (labelPdfUrl) dans l'email de clôture
 * - ✅ NOUVEAU: CC conseil syndical optionnel sur emails de clôture et refus (demandes collectives)
 * - ✅ NOUVEAU: Rebranding O'Copro — logo en banner, sections Résidence+Identifiants fusionnées, footer mis à jour
 * 
 * src/services/emailService.ts
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { logger } from '@/utils/logger';

const getEmailFn = () =>
  httpsCallable<Record<string, any>, { success: boolean }>(
    getFunctions(getApp(), 'europe-west1'),
    'sendEmailCF'
  );

/**
 * Envoie un document mail brut via la Cloud Function sendEmailCF.
 * Utilisé par les appels directs (support, invitations, budget, etc.).
 * Retire `createdAt` du payload : la CF ajoute sa propre valeur serveur.
 */
export const sendRawEmail = async (mailDoc: Record<string, any>): Promise<void> => {
  const { createdAt, ...payload } = mailDoc;
  await getEmailFn()(payload);
};

// ============================================================================
// TYPES
// ============================================================================

export interface EmailAttachment {
  filename: string;
  path: string; // URL de la pièce jointe
}

export interface RequestEmailData {
  // Infos demande
  requestId: string;
  requestType: string;
  requestTitle: string;
  requestDescription: string;
  problemType?: string;
  photoURL?: string; // URL de la photo (sera ajoutée en pièce jointe)
  
  // ✅ NOUVEAU: Complément CS (ajouté avant transmission au syndic)
  csComment?: string;             // Texte libre ajouté par le CS
  csPhotoURLs?: string[];         // Photos ajoutées par le CS
  
  // Infos demandeur
  submitterName: string;
  submitterEmail: string;
  submitterCategory: string;
  submitterIdentifier?: string; // ✅ ID copropriétaire ou locataire
  submitterApartment?: string;
  submitterBuilding?: string | null; // Bâtiment du demandeur (si résidence multi-bâtiments)
  requestBuilding?: string | null;   // Bâtiment concerné par la demande collective
  
  // Infos résidence
  residenceName: string;
  residenceAddress?: string;
  residenceCode?: string;        // ✅ Identifiant O'Copro
  syndicReference?: string;      // ✅ Référence métier copropriété auprès du syndic (ex: SYN-2024-JRD-001)
  
  // Infos action (pour changement de statut)
  actionBy?: string;
  actionReason?: string;
  newStatus?: string;
}

export interface SendEmailOptions {
  to: string[];
  cc?: string[];
  subject: string;
  html: string;
  text?: string;
  attachments?: EmailAttachment[];
}

// ============================================================================
// CONSTANTES
// ============================================================================

const REQUEST_TYPE_LABELS: Record<string, string> = {
  label: 'Étiquette boîte aux lettres',
  fiber: 'Accès local fibre',
  problem: 'Signalement de problème'
};

const PROBLEM_TYPE_LABELS: Record<string, string> = {
  collective: 'Parties communes',
  private: 'Privatif'
};

const CATEGORY_LABELS: Record<string, string> = {
  tenant: 'Locataire',
  owner: 'Copropriétaire',
  council: 'Conseil Syndical',
  syndic: 'Syndic',
  gardien: 'Gardien',
  admin: 'Administrateur'
};

const LOGO_URL = 'https://firebasestorage.googleapis.com/v0/b/mycopro-fb5fd.firebasestorage.app/o/assets%2Femail%2Focopro_logo.jpg?alt=media';

// ============================================================================
// FONCTION PRINCIPALE D'ENVOI
// ============================================================================

/**
 * Envoie un email via la collection Firestore `mail`
 * L'extension Firebase "Trigger Email" traite automatiquement les documents
 */
export const sendEmail = async (options: SendEmailOptions): Promise<boolean> => {
  try {
    const payload: Record<string, any> = {
      to: options.to,
      cc: options.cc || [],
      message: {
        subject: options.subject,
        html: options.html,
        text: options.text || stripHtml(options.html),
      },
    };

    if (options.attachments && options.attachments.length > 0) {
      payload.message.attachments = options.attachments;
    }

    await getEmailFn()(payload);

    logger.email('Email queued for:', options.to.join(', '));
    return true;
  } catch (error) {
    console.error('[EmailService] Error queuing email:', error);
    return false;
  }
};

// ============================================================================
// HELPER: PRÉPARER LES PIÈCES JOINTES
// ============================================================================

const prepareAttachments = (photoURL?: string): EmailAttachment[] => {
  if (!photoURL) return [];
  
  // Extraire le nom du fichier depuis l'URL
  const urlParts = photoURL.split('/');
  const filenameWithParams = urlParts[urlParts.length - 1];
  const filename = filenameWithParams.split('?')[0] || 'photo.jpg';
  
  // Nettoyer le nom (décoder les %2F etc.)
  const cleanFilename = decodeURIComponent(filename).split('/').pop() || 'photo.jpg';
  
  return [{
    filename: cleanFilename,
    path: photoURL
  }];
};

// ============================================================================
// EMAILS POUR LES DEMANDES
// ============================================================================

/**
 * Email envoyé au syndic quand une demande lui est transmise
 * + copie au demandeur + copie CS (si csEmail renseigné)
 * ✅ CORRIGÉ: Inclut les photos CS en pièces jointes supplémentaires
 * ✅ NOUVEAU: Email CS en copie (CC) si renseigné dans les settings résidence
 */
export const sendRequestToSyndicEmail = async (
  syndicEmail: string,
  data: RequestEmailData,
  csEmail?: string
): Promise<boolean> => {
  const subject = `[O'Copro] Nouvelle demande - ${data.requestTitle}`;
  
  const html = generateRequestEmailHtml({
    title: 'Nouvelle demande transmise',
    subtitle: `Une demande vous a été transmise par ${data.submitterName}`,
    data,
    showFullDetails: true,
    actionMessage: 'Veuillez traiter cette demande dans les meilleurs délais.'
  });
  
  // ✅ NOUVEAU: Assembler les pièces jointes (photo originale + photos CS)
  const allAttachments: EmailAttachment[] = [
    ...prepareAttachments(data.photoURL),
    ...(data.csPhotoURLs || []).map((url, i) => ({
      filename: `photo_cs_${i + 1}.jpg`,
      path: url
    }))
  ];
  
  // ✅ NOUVEAU: CC = demandeur + email CS si renseigné
  const ccList: string[] = [];
  if (data.submitterEmail) ccList.push(data.submitterEmail);
  if (csEmail && csEmail !== syndicEmail && !ccList.includes(csEmail)) ccList.push(csEmail);
  
  return sendEmail({
    to: [syndicEmail],
    cc: ccList.length > 0 ? ccList : [],
    subject,
    html,
    attachments: allAttachments.length > 0 ? allAttachments : undefined
  });
};

/**
 * Email envoyé au bailleur social quand une demande lui est transmise
 * + copie au demandeur
 */
export const sendRequestToBailleurEmail = async (
  bailleurEmail: string,
  bailleurName: string,
  data: RequestEmailData
): Promise<boolean> => {
  const subject = `[O'Copro] Demande locataire - ${data.requestTitle}`;
  
  const html = generateRequestEmailHtml({
    title: 'Demande de locataire',
    subtitle: `Votre locataire ${data.submitterName} a soumis une demande`,
    data,
    showFullDetails: true,
    actionMessage: `En tant que bailleur social, vous êtes responsable du traitement de cette demande concernant le logement de ${data.submitterName}.`,
    recipientName: bailleurName
  });
  
  return sendEmail({
    to: [bailleurEmail],
    cc: data.submitterEmail ? [data.submitterEmail] : [],
    subject,
    html,
    attachments: prepareAttachments(data.photoURL)
  });
};

/**
 * Email de confirmation au demandeur quand sa demande est validée
 */
export const sendRequestValidatedEmail = async (
  data: RequestEmailData
): Promise<boolean> => {
  if (!data.submitterEmail) return false;
  
  const subject = `[O'Copro] Demande validée - ${data.requestTitle}`;
  
  const html = generateRequestEmailHtml({
    title: 'Votre demande a été validée ✓',
    subtitle: `Bonne nouvelle ! Votre demande a été validée par ${data.actionBy || 'le conseil syndical'}`,
    data,
    showFullDetails: false,
    statusBadge: { label: 'Validée', color: '#10b981' },
    actionMessage: 'Le conseil syndical va traiter votre demande. Vous serez notifié(e) lorsqu\'elle sera clôturée.'
  });
  
  return sendEmail({
    to: [data.submitterEmail],
    subject,
    html
  });
};

/**
 * Email de notification au demandeur quand sa demande est transmise au syndic
 */
export const sendRequestTransmittedEmail = async (
  data: RequestEmailData,
  transmittedTo: 'syndic' | 'bailleur'
): Promise<boolean> => {
  if (!data.submitterEmail) return false;
  
  const recipientLabel = transmittedTo === 'syndic' ? 'au syndic' : 'à votre bailleur social';
  const subject = `[O'Copro] Demande transmise ${recipientLabel} - ${data.requestTitle}`;
  
  const html = generateRequestEmailHtml({
    title: `Votre demande a été transmise ${recipientLabel}`,
    subtitle: `Votre demande est maintenant entre les mains ${transmittedTo === 'syndic' ? 'du syndic' : 'de votre bailleur'}`,
    data,
    showFullDetails: false,
    statusBadge: { label: 'Transmise', color: '#3b82f6' },
    actionMessage: `${transmittedTo === 'syndic' ? 'Le syndic' : 'Votre bailleur'} va examiner votre demande et vous tiendra informé(e) de la suite.`
  });
  
  return sendEmail({
    to: [data.submitterEmail],
    subject,
    html
  });
};

/**
 * Email de notification au demandeur quand sa demande est rejetée
 * ✅ NOUVEAU: Paramètre cc optionnel pour copie au conseil syndical (demandes collectives)
 */
export const sendRequestRejectedEmail = async (
  data: RequestEmailData,
  cc?: string[]
): Promise<boolean> => {
  if (!data.submitterEmail) return false;
  
  const subject = `[O'Copro] Demande refusée - ${data.requestTitle}`;
  
  const html = generateRequestEmailHtml({
    title: 'Votre demande a été refusée',
    subtitle: `Nous sommes désolés, votre demande n'a pas pu être acceptée`,
    data,
    showFullDetails: false,
    statusBadge: { label: 'Refusée', color: '#ef4444' },
    actionMessage: data.actionReason 
      ? `Motif du refus : ${data.actionReason}`
      : 'Contactez le conseil syndical pour plus d\'informations.',
    showReason: true
  });
  
  return sendEmail({
    to: [data.submitterEmail],
    // ✅ NOUVEAU: CC conseil syndical si renseigné (demandes collectives uniquement)
    cc: cc && cc.length > 0 ? cc : [],
    subject,
    html
  });
};

/**
 * Email de notification au demandeur quand sa demande est clôturée
 * ✅ CORRIGÉ: Attache le PDF de l'étiquette si labelPdfUrl est présent
 * ✅ NOUVEAU: Paramètre cc optionnel pour copie au conseil syndical (demandes collectives)
 */
export const sendRequestClosedEmail = async (
  data: RequestEmailData,
  cc?: string[]
): Promise<boolean> => {
  if (!data.submitterEmail) return false;
  
  const subject = `[O'Copro] Demande clôturée - ${data.requestTitle}`;
  
  const html = generateRequestEmailHtml({
    title: 'Votre demande a été clôturée ✓',
    subtitle: 'Votre demande a été traitée avec succès',
    data,
    showFullDetails: false,
    statusBadge: { label: 'Clôturée', color: '#6b7280' },
    actionMessage: data.actionReason 
      ? `Commentaire de clôture : ${data.actionReason}`
      : 'Merci d\'avoir utilisé O\'Copro !'
  });
  
  // ✅ NOUVEAU: Préparer les pièces jointes (photo + PDF étiquette si disponible)
  const attachments: EmailAttachment[] = [];
  // Ajouter la photo si présente
  if (data.photoURL) {
    attachments.push(...prepareAttachments(data.photoURL));
  }
  // ✅ Ajouter le PDF étiquette si disponible (pour les demandes de type label)
  if ((data as any).labelPdfUrl) {
    attachments.push({
      filename: `etiquette-${data.requestTitle.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`,
      path: (data as any).labelPdfUrl
    });
  }
  
  return sendEmail({
    to: [data.submitterEmail],
    // ✅ NOUVEAU: CC conseil syndical si renseigné (demandes collectives uniquement)
    cc: cc && cc.length > 0 ? cc : [],
    subject,
    html,
    attachments: attachments.length > 0 ? attachments : undefined
  });
};

// ============================================================================
// GÉNÉRATION HTML
// ============================================================================

interface GenerateEmailOptions {
  title: string;
  subtitle: string;
  data: RequestEmailData;
  showFullDetails: boolean;
  statusBadge?: { label: string; color: string };
  actionMessage?: string;
  recipientName?: string;
  showReason?: boolean;
}

const generateRequestEmailHtml = (options: GenerateEmailOptions): string => {
  const { title, subtitle, data, showFullDetails, statusBadge, actionMessage } = options;
  
  const typeLabel = REQUEST_TYPE_LABELS[data.requestType] || data.requestType;
  const problemLabel = data.problemType ? PROBLEM_TYPE_LABELS[data.problemType] : null;
  const categoryLabel = CATEGORY_LABELS[data.submitterCategory] || data.submitterCategory;
  
  // ✅ Pour l'identifiant, un membre du conseil syndical est avant tout un copropriétaire
  const identifierLabel = ['council'].includes(data.submitterCategory) 
    ? 'Copropriétaire' 
    : categoryLabel;
  
  // ✅ Photo preview section (si présente)
  const photoSection = data.photoURL ? `
    <tr>
      <td style="padding: 16px 24px 0;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #fef3c7; border-radius: 12px; border: 1px solid #fcd34d;">
          <tr>
            <td style="padding: 16px; text-align: center;">
              <p style="margin: 0 0 12px; color: #92400e; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">📷 Photo jointe</p>
              <img src="${data.photoURL}" alt="Photo de la demande" style="max-width: 100%; max-height: 300px; border-radius: 8px; border: 1px solid #e5e7eb;" />
              <p style="margin: 12px 0 0; color: #78716c; font-size: 11px;">La photo est également disponible en pièce jointe.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  ` : '';
  
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f3f4f6; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f3f4f6;">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width: 600px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          
          <!-- ✅ Header avec logo O'Copro -->
          <tr>
            <td style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 28px 24px; text-align: center;">
              <img src="${LOGO_URL}" alt="O'Copro" style="height: 44px; max-width: 200px; margin-bottom: 8px; border-radius: 8px;" />
              <p style="margin: 0; color: rgba(255,255,255,0.9); font-size: 13px;">Gestion de copropriété</p>
            </td>
          </tr>
          
          <!-- Title -->
          <tr>
            <td style="padding: 32px 24px 16px;">
              ${statusBadge ? `
                <span style="display: inline-block; background-color: ${statusBadge.color}; color: #ffffff; font-size: 12px; font-weight: 600; padding: 4px 12px; border-radius: 9999px; margin-bottom: 12px;">
                  ${statusBadge.label}
                </span>
              ` : ''}
              <h2 style="margin: 0 0 8px; color: #111827; font-size: 20px; font-weight: 700;">
                ${title}
              </h2>
              <p style="margin: 0; color: #6b7280; font-size: 14px;">
                ${subtitle}
              </p>
            </td>
          </tr>
          
          <!-- ✅ SECTION FUSIONNÉE: Résidence + Identifiants (visible pour le syndic) -->
          ${showFullDetails ? `
          <tr>
            <td style="padding: 0 24px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #dbeafe; border-radius: 12px; border: 1px solid #93c5fd;">
                <tr>
                  <td style="padding: 16px;">
                    <p style="margin: 0 0 10px; color: #1e40af; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">🏢 Résidence & identifiants</p>
                    <p style="margin: 0; color: #1e3a8a; font-size: 15px; font-weight: 700;">${data.residenceName}</p>
                    ${data.residenceAddress ? `<p style="margin: 4px 0 0; color: #3b82f6; font-size: 13px;">${data.residenceAddress}</p>` : ''}
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-top: 12px; padding-top: 12px; border-top: 1px dashed #93c5fd;">
                      <tr>
                        <td width="50%" style="padding: 4px 0;">
                          <p style="margin: 0; color: #1e40af; font-size: 11px; text-transform: uppercase;">Réf. Syndic</p>
                          <p style="margin: 0; color: #1e3a8a; font-size: 15px; font-weight: 700; font-family: monospace;">${data.syndicReference || 'Non défini'}</p>
                        </td>
                        <td width="50%" style="padding: 4px 0;">
                          <p style="margin: 0; color: #1e40af; font-size: 11px; text-transform: uppercase;">ID O'Copro</p>
                          <p style="margin: 0; color: #1e3a8a; font-size: 15px; font-weight: 700; font-family: monospace;">${data.residenceCode || 'Non défini'}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          ` : ''}
          
          <!-- Request Info Card -->
          <tr>
            <td style="padding: 16px 24px 0;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f9fafb; border-radius: 12px; border: 1px solid #e5e7eb;">
                <tr>
                  <td style="padding: 20px;">
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                      <!-- Type de demande -->
                      <tr>
                        <td style="padding-bottom: 12px;">
                          <p style="margin: 0 0 4px; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Type de demande</p>
                          <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">
                            ${typeLabel}${problemLabel ? ` - ${problemLabel}` : ''}
                          </p>
                        </td>
                      </tr>
                      
                      <!-- Bâtiment concerné (si multi-bâtiments) -->
                      ${data.requestBuilding ? `
                      <tr>
                        <td style="padding-bottom: 12px;">
                          <p style="margin: 0 0 4px; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Bâtiment concerné</p>
                          <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">
                            🏗️ ${data.requestBuilding === 'all' ? 'Toute la résidence' : data.requestBuilding}
                          </p>
                        </td>
                      </tr>
                      ` : ''}

                      <!-- Titre -->
                      <tr>
                        <td style="padding-bottom: 12px;">
                          <p style="margin: 0 0 4px; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Titre</p>
                          <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">${data.requestTitle}</p>
                        </td>
                      </tr>
                      
                      ${showFullDetails ? `
                        <!-- Description -->
                        <tr>
                          <td style="padding-bottom: 0;">
                            <p style="margin: 0 0 4px; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Description</p>
                            <p style="margin: 0; color: #374151; font-size: 14px; line-height: 1.5; white-space: pre-wrap;">${data.requestDescription}</p>
                          </td>
                        </tr>
                      ` : ''}
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          ${showFullDetails ? `
            <!-- ✅ DEMANDEUR INFO (avec identifiant bien visible) -->
            <tr>
              <td style="padding: 16px 24px 0;">
                <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #fef3c7; border-radius: 12px; border: 1px solid #fcd34d;">
                  <tr>
                    <td style="padding: 16px;">
                      <p style="margin: 0 0 12px; color: #92400e; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">👤 Informations demandeur</p>
                      <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                        <!-- ✅ Identifiant en évidence -->
                        ${data.submitterIdentifier ? `
                        <tr>
                          <td colspan="2" style="padding: 8px 12px; background-color: #fbbf24; border-radius: 8px; margin-bottom: 8px;">
                            <p style="margin: 0; color: #78350f; font-size: 11px; text-transform: uppercase;">Identifiant ${identifierLabel}</p>
                            <p style="margin: 0; color: #1c1917; font-size: 18px; font-weight: 700; font-family: monospace;">${data.submitterIdentifier}</p>
                          </td>
                        </tr>
                        <tr><td colspan="2" style="height: 8px;"></td></tr>
                        ` : ''}
                        <tr>
                          <td width="50%" style="padding: 6px 0;">
                            <p style="margin: 0 0 2px; color: #78716c; font-size: 12px;">Nom</p>
                            <p style="margin: 0; color: #1c1917; font-size: 14px; font-weight: 500;">${data.submitterName}</p>
                          </td>
                          <td width="50%" style="padding: 6px 0;">
                            <p style="margin: 0 0 2px; color: #78716c; font-size: 12px;">Catégorie</p>
                            <p style="margin: 0; color: #1c1917; font-size: 14px; font-weight: 500;">${categoryLabel}</p>
                          </td>
                        </tr>
                        <tr><td colspan="2" style="height: 6px;"></td></tr>
                        <tr>
                          <td width="50%" style="padding: 6px 0;">
                            <p style="margin: 0 0 2px; color: #78716c; font-size: 12px;">Appartement</p>
                            <p style="margin: 0; color: #1c1917; font-size: 14px; font-weight: 500;">${data.submitterApartment || '-'}</p>
                          </td>
                          <td width="50%" style="padding: 6px 0;">
                            <p style="margin: 0 0 2px; color: #78716c; font-size: 12px;">Email</p>
                            <p style="margin: 0; color: #1c1917; font-size: 14px; font-weight: 500;">${data.submitterEmail || '-'}</p>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            
            <!-- ✅ SUPPRIMÉ: Résidence Info — fusionné dans la section "Résidence & identifiants" ci-dessus -->
          ` : ''}
          
          <!-- ✅ Photo (si présente) -->
          ${photoSection}
          
          <!-- ✅ NOUVEAU: Complément CS (si présent) -->
          ${(data as any).csComment || ((data as any).csPhotoURLs && (data as any).csPhotoURLs.length > 0) ? `
            <tr>
              <td style="padding: 16px 24px 0;">
                <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f3e8ff; border-radius: 12px; border: 1px solid #c084fc;">
                  <tr>
                    <td style="padding: 16px;">
                      <p style="margin: 0 0 12px; color: #6b21a8; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">💬 Complément du Conseil Syndical</p>
                      ${(data as any).csComment ? `<p style="margin: 0 0 12px; color: #4c1d95; font-size: 14px; line-height: 1.5; white-space: pre-wrap;">${(data as any).csComment}</p>` : ''}
                      ${((data as any).csPhotoURLs && (data as any).csPhotoURLs.length > 0) ? `
                        <p style="margin: 0 0 8px; color: #6b21a8; font-size: 12px;">📷 ${(data as any).csPhotoURLs.length} photo(s) jointe(s) par le CS (voir pièces jointes)</p>
                        ${(data as any).csPhotoURLs.map((url: string) => `
                          <img src="${url}" alt="Photo CS" style="max-width: 100%; max-height: 200px; border-radius: 8px; border: 1px solid #e5e7eb; margin-bottom: 8px;" />
                        `).join('')}
                      ` : ''}
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          ` : ''}
          
          ${actionMessage ? `
            <!-- Action Message -->
            <tr>
              <td style="padding: 24px;">
                <p style="margin: 0; color: #374151; font-size: 14px; line-height: 1.6; background-color: #f0fdf4; border-left: 4px solid #10b981; padding: 12px 16px; border-radius: 0 8px 8px 0;">
                  ${actionMessage}
                </p>
              </td>
            </tr>
          ` : ''}
          
          <!-- Footer -->
          <tr>
            <td style="padding: 24px; background-color: #f9fafb; border-top: 1px solid #e5e7eb; text-align: center;">
              <p style="margin: 0 0 8px; color: #6b7280; font-size: 12px;">
                Cet email a été envoyé automatiquement par O'Copro.
              </p>
              <p style="margin: 0; color: #9ca3af; font-size: 11px;">
                © ${new Date().getFullYear()} O'Copro - Gestion de copropriété simplifiée
              </p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Supprime les tags HTML pour générer la version texte brut
 */
const stripHtml = (html: string): string => {
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// ============================================================================
// ✅ NOUVEAU: EMAIL NOTIFICATION CS — NOUVELLE DEMANDE
// ============================================================================

/**
 * Email envoyé au CS lorsqu'une demande est soumise (étiquette, fibre, problème collectif).
 * Destinataires : adresse CS de groupe OU emails personnels des membres du CS.
 */
export const sendNewRequestToCSEmail = async (
  recipients: string[],
  data: RequestEmailData
): Promise<boolean> => {
  if (recipients.length === 0) return false;

  const subject = `[O'Copro] Nouvelle demande à traiter - ${data.requestTitle}`;

  const html = generateRequestEmailHtml({
    title: 'Nouvelle demande à traiter',
    subtitle: `${data.submitterName} a soumis une demande nécessitant votre attention`,
    data,
    showFullDetails: true,
    actionMessage: 'Connectez-vous à O\'Copro pour traiter cette demande.'
  });

  const attachments = prepareAttachments(data.photoURL);

  return sendEmail({
    to: recipients,
    subject,
    html,
    attachments: attachments.length > 0 ? attachments : undefined
  });
};

// ============================================================================
// EMAIL NOTIFICATION ADMIN — NOUVELLE RÉSIDENCE CRÉÉE
// ============================================================================

export const sendNewResidenceNotificationEmail = async (data: {
  userName: string;
  userEmail: string;
  userCategory: string;
  residenceCode: string;
  residenceName: string;
  residenceAddress: string;
  residencePostalCode: string;
  residenceCity: string;
}): Promise<boolean> => {
  const categoryLabel = CATEGORY_LABELS[data.userCategory] || data.userCategory;
  const fullAddress = [data.residenceAddress, data.residencePostalCode, data.residenceCity].filter(Boolean).join(', ');

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Nouvelle résidence créée</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f3f4f6; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f3f4f6;">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width: 600px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">

          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 28px 24px; text-align: center;">
              <img src="${LOGO_URL}" alt="O'Copro" style="height: 44px; max-width: 200px; margin-bottom: 8px; border-radius: 8px;" />
              <p style="margin: 0; color: rgba(255,255,255,0.9); font-size: 13px;">Gestion de copropriété</p>
            </td>
          </tr>

          <!-- Title -->
          <tr>
            <td style="padding: 32px 24px 16px;">
              <h2 style="margin: 0 0 8px; color: #111827; font-size: 20px; font-weight: 700;">🏢 Nouvelle résidence créée</h2>
              <p style="margin: 0; color: #6b7280; font-size: 14px;">Une nouvelle résidence vient d'être enregistrée sur O'Copro.</p>
            </td>
          </tr>

          <!-- Résidence -->
          <tr>
            <td style="padding: 0 24px 16px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #dbeafe; border-radius: 12px; border: 1px solid #93c5fd;">
                <tr>
                  <td style="padding: 16px;">
                    <p style="margin: 0 0 10px; color: #1e40af; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">🏢 Résidence</p>
                    <p style="margin: 0 0 4px; color: #1e3a8a; font-size: 16px; font-weight: 700;">${data.residenceName}</p>
                    <p style="margin: 0 0 12px; color: #3b82f6; font-size: 13px;">${fullAddress}</p>
                    <p style="margin: 0; color: #1e40af; font-size: 11px; text-transform: uppercase;">Identifiant O'Copro</p>
                    <p style="margin: 0; color: #1e3a8a; font-size: 18px; font-weight: 700; font-family: monospace;">${data.residenceCode}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Créateur -->
          <tr>
            <td style="padding: 0 24px 24px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #fef3c7; border-radius: 12px; border: 1px solid #fcd34d;">
                <tr>
                  <td style="padding: 16px;">
                    <p style="margin: 0 0 12px; color: #92400e; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">👤 Créateur</p>
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                      <tr>
                        <td width="50%" style="padding: 4px 0;">
                          <p style="margin: 0 0 2px; color: #78716c; font-size: 12px;">Nom</p>
                          <p style="margin: 0; color: #1c1917; font-size: 14px; font-weight: 600;">${data.userName}</p>
                        </td>
                        <td width="50%" style="padding: 4px 0;">
                          <p style="margin: 0 0 2px; color: #78716c; font-size: 12px;">Catégorie</p>
                          <p style="margin: 0; color: #1c1917; font-size: 14px; font-weight: 600;">${categoryLabel}</p>
                        </td>
                      </tr>
                      <tr><td colspan="2" style="height: 8px;"></td></tr>
                      <tr>
                        <td colspan="2" style="padding: 4px 0;">
                          <p style="margin: 0 0 2px; color: #78716c; font-size: 12px;">Email</p>
                          <p style="margin: 0; color: #1c1917; font-size: 14px; font-weight: 600;">${data.userEmail}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 24px; background-color: #f9fafb; border-top: 1px solid #e5e7eb; text-align: center;">
              <p style="margin: 0 0 8px; color: #6b7280; font-size: 12px;">Cet email a été envoyé automatiquement par O'Copro.</p>
              <p style="margin: 0; color: #9ca3af; font-size: 11px;">© ${new Date().getFullYear()} O'Copro - Gestion de copropriété simplifiée</p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`.trim();

  return sendEmail({
    to: ['contact@ocopro.fr'],
    subject: `[O'Copro] Nouvelle résidence créée — ${data.residenceName} (${data.residenceCode})`,
    html,
  });
};

// ============================================================================
// EMAIL MEMBRE — Inscription validée
// ============================================================================

export const sendMemberApprovedEmail = async ({
  memberEmail,
  memberName,
  residenceName,
  category,
}: {
  memberEmail: string;
  memberName: string;
  residenceName: string;
  category?: string;
}): Promise<boolean> => {
  const CATEGORY_FR: Record<string, string> = {
    tenant: 'Locataire', owner: 'Copropriétaire', council: 'Conseil syndical', gardien: 'Gardien',
  };
  const categoryLabel = CATEGORY_FR[category || ''] || category || '';

  const html = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;background-color:#f3f4f6;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#f3f4f6;">
    <tr><td align="center" style="padding:40px 20px;">
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:600px;background-color:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 6px rgba(0,0,0,0.1);">
        <tr>
          <td style="background:#ffffff;padding:28px 24px;text-align:center;border-bottom:1px solid #e5e7eb;">
            <img src="${LOGO_URL}" alt="O'Copro" style="height:44px;max-width:200px;margin-bottom:8px;border-radius:8px;" />
            <p style="margin:0;color:#6b7280;font-size:13px;">Gestion de copropriété</p>
          </td>
        </tr>
        <tr>
          <td style="padding:32px 24px 16px;">
            <span style="display:inline-block;background-color:#10b981;color:#ffffff;font-size:12px;font-weight:600;padding:4px 12px;border-radius:9999px;margin-bottom:12px;">✅ Inscription validée</span>
            <h2 style="margin:0 0 8px;color:#111827;font-size:20px;font-weight:700;">Bienvenue dans la résidence !</h2>
            <p style="margin:0;color:#6b7280;font-size:14px;">Bonjour <strong>${memberName}</strong>, votre inscription a été approuvée.</p>
          </td>
        </tr>
        <tr>
          <td style="padding:0 24px 16px;">
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#d1fae5;border-radius:12px;border:1px solid #6ee7b7;">
              <tr><td style="padding:16px;">
                <p style="margin:0 0 6px;color:#065f46;font-size:12px;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">🏢 Résidence</p>
                <p style="margin:0;color:#064e3b;font-size:15px;font-weight:700;">${residenceName}</p>
                ${categoryLabel ? `<p style="margin:4px 0 0;color:#059669;font-size:13px;">${categoryLabel}</p>` : ''}
              </td></tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding:0 24px 24px;">
            <p style="margin:0;color:#374151;font-size:14px;line-height:1.6;">
              Vous pouvez maintenant accéder à toutes les fonctionnalités de l'application : demandes, documents, calendrier et messagerie.
            </p>
          </td>
        </tr>
        <tr>
          <td style="background-color:#f9fafb;padding:20px 24px;border-top:1px solid #e5e7eb;text-align:center;">
            <p style="margin:0;color:#9ca3af;font-size:12px;">O'Copro — Gestion de copropriété</p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

  return sendEmail({
    to: [memberEmail],
    subject: `[O'Copro] Votre inscription a été validée — ${residenceName}`,
    html,
  });
};

// ============================================================================
// EMAIL MEMBRE — Inscription refusée
// ============================================================================

export const sendMemberRejectedEmail = async ({
  memberEmail,
  memberName,
  residenceName,
  reason,
  subject,
}: {
  memberEmail: string;
  memberName: string;
  residenceName: string;
  reason: string;
  subject?: string;
}): Promise<boolean> => {
  const html = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;background-color:#f3f4f6;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#f3f4f6;">
    <tr><td align="center" style="padding:40px 20px;">
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:600px;background-color:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 6px rgba(0,0,0,0.1);">
        <tr>
          <td style="background:#ffffff;padding:28px 24px;text-align:center;border-bottom:1px solid #e5e7eb;">
            <img src="${LOGO_URL}" alt="O'Copro" style="height:44px;max-width:200px;margin-bottom:8px;border-radius:8px;" />
            <p style="margin:0;color:#6b7280;font-size:13px;">Gestion de copropriété</p>
          </td>
        </tr>
        <tr>
          <td style="padding:32px 24px 16px;">
            <span style="display:inline-block;background-color:#ef4444;color:#ffffff;font-size:12px;font-weight:600;padding:4px 12px;border-radius:9999px;margin-bottom:12px;">❌ Inscription refusée</span>
            <h2 style="margin:0 0 8px;color:#111827;font-size:20px;font-weight:700;">Demande d'inscription refusée</h2>
            <p style="margin:0;color:#6b7280;font-size:14px;">Bonjour <strong>${memberName}</strong>, votre demande pour rejoindre <strong>${residenceName}</strong> n'a pas pu être acceptée.</p>
          </td>
        </tr>
        <tr>
          <td style="padding:0 24px 16px;">
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#fee2e2;border-radius:12px;border:1px solid #fca5a5;">
              <tr><td style="padding:16px;">
                <p style="margin:0 0 8px;color:#991b1b;font-size:12px;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">📋 Motif du refus</p>
                <p style="margin:0;color:#7f1d1d;font-size:14px;line-height:1.6;">${reason}</p>
              </td></tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding:0 24px 24px;">
            <p style="margin:0;color:#374151;font-size:14px;line-height:1.6;">
              Si vous souhaitez plus d'informations, rapprochez-vous directement du <strong>Conseil Syndical</strong> de votre résidence ou suivez les instructions sur l'application pour valider votre inscription via le <strong>code de parrainage</strong>.
            </p>
          </td>
        </tr>
        <tr>
          <td style="background-color:#f9fafb;padding:20px 24px;border-top:1px solid #e5e7eb;text-align:center;">
            <p style="margin:0;color:#9ca3af;font-size:12px;">O'Copro — Gestion de copropriété</p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

  return sendEmail({
    to: [memberEmail],
    subject: subject
      ? `[O'Copro] ${subject} — ${residenceName}`
      : `[O'Copro] Votre demande d'inscription — ${residenceName}`,
    html,
  });
};

// ============================================================================
// NOTIFICATION CS — Nouvelle inscription en attente
// ============================================================================

const CATEGORY_FR: Record<string, string> = {
  tenant: 'Locataire',
  owner: 'Copropriétaire',
  council: 'Conseil syndical',
  gardien: 'Gardien',
};

export const sendNewMemberPendingNotification = async ({
  csEmails,
  residenceName,
  memberName,
  memberEmail,
  memberCategory,
  memberApartment,
  memberBuilding,
  invitedBy,
}: {
  csEmails: string[];
  residenceName: string;
  memberName: string;
  memberEmail: string;
  memberCategory: string;
  memberApartment?: string;
  memberBuilding?: string;
  invitedBy?: { name: string; category?: string; apartment?: string; building?: string };
}): Promise<boolean> => {
  const categoryLabel = (c?: string) => CATEGORY_FR[c || ''] || c || '';

  const memberLines = [
    `<p style="margin:0;color:#1e3a8a;font-size:15px;font-weight:700;">${memberName}</p>`,
    `<p style="margin:4px 0 0;color:#3b82f6;font-size:13px;">${memberEmail}</p>`,
    `<p style="margin:4px 0 0;color:#3b82f6;font-size:13px;">${categoryLabel(memberCategory)}</p>`,
    memberBuilding ? `<p style="margin:4px 0 0;color:#3b82f6;font-size:13px;">Bâtiment ${memberBuilding}</p>` : '',
    memberApartment ? `<p style="margin:4px 0 0;color:#3b82f6;font-size:13px;">Appartement ${memberApartment}</p>` : '',
  ].join('');

  const invitantSection = invitedBy ? `
        <tr>
          <td style="padding:0 24px 16px;">
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#f3e8ff;border-radius:12px;border:1px solid #d8b4fe;">
              <tr><td style="padding:16px;">
                <p style="margin:0 0 6px;color:#7e22ce;font-size:12px;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">🤝 Invité par</p>
                <p style="margin:0;color:#581c87;font-size:15px;font-weight:700;">${invitedBy.name}</p>
                ${invitedBy.category ? `<p style="margin:4px 0 0;color:#7c3aed;font-size:13px;">${categoryLabel(invitedBy.category)}</p>` : ''}
                ${invitedBy.building ? `<p style="margin:4px 0 0;color:#7c3aed;font-size:13px;">Bâtiment ${invitedBy.building}</p>` : ''}
                ${invitedBy.apartment ? `<p style="margin:4px 0 0;color:#7c3aed;font-size:13px;">Appartement ${invitedBy.apartment}</p>` : ''}
              </td></tr>
            </table>
          </td>
        </tr>` : '';

  const html = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;background-color:#f3f4f6;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#f3f4f6;">
    <tr><td align="center" style="padding:40px 20px;">
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:600px;background-color:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 6px rgba(0,0,0,0.1);">

        <!-- Header -->
        <tr>
          <td style="background:#ffffff;padding:28px 24px;text-align:center;border-bottom:1px solid #e5e7eb;">
            <img src="${LOGO_URL}" alt="O'Copro" style="height:44px;max-width:200px;margin-bottom:8px;border-radius:8px;" />
            <p style="margin:0;color:#6b7280;font-size:13px;">Gestion de copropriété</p>
          </td>
        </tr>

        <!-- Title -->
        <tr>
          <td style="padding:32px 24px 16px;">
            <span style="display:inline-block;background-color:#3b82f6;color:#ffffff;font-size:12px;font-weight:600;padding:4px 12px;border-radius:9999px;margin-bottom:12px;">🙋 Nouvelle inscription</span>
            <h2 style="margin:0 0 8px;color:#111827;font-size:20px;font-weight:700;">Demande d'inscription en attente</h2>
            <p style="margin:0;color:#6b7280;font-size:14px;">Un nouveau résident souhaite rejoindre <strong>${residenceName}</strong>.</p>
          </td>
        </tr>

        <!-- Member info -->
        <tr>
          <td style="padding:0 24px 16px;">
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#dbeafe;border-radius:12px;border:1px solid #93c5fd;">
              <tr><td style="padding:16px;">
                <p style="margin:0 0 6px;color:#1e40af;font-size:12px;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">👤 Nouveau membre</p>
                ${memberLines}
              </td></tr>
            </table>
          </td>
        </tr>

        <!-- Invitant (si applicable) -->
        ${invitantSection}

        <!-- Action -->
        <tr>
          <td style="padding:0 24px 24px;">
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#f0fdf4;border-radius:12px;border:1px solid #86efac;">
              <tr><td style="padding:16px;">
                <p style="margin:0 0 6px;color:#15803d;font-size:12px;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">✅ Action requise</p>
                <p style="margin:0;color:#14532d;font-size:14px;line-height:1.5;">
                  Connectez-vous à O'Copro et rendez-vous dans la section <strong>Inscriptions</strong> pour valider ou refuser cette demande.
                </p>
              </td></tr>
            </table>
          </td>
        </tr>

        <!-- Footer -->
        <tr>
          <td style="background-color:#f9fafb;padding:20px 24px;border-top:1px solid #e5e7eb;text-align:center;">
            <p style="margin:0;color:#9ca3af;font-size:12px;">O'Copro — Gestion de copropriété</p>
          </td>
        </tr>

      </table>
    </td></tr>
  </table>
</body>
</html>`;

  return sendEmail({
    to: csEmails,
    subject: `[O'Copro] Nouvelle demande d'inscription — ${residenceName}`,
    html,
  });
};

// ============================================================================
// NOTIFICATION CS — Bailleur social non renseigné
// ============================================================================

export const sendSocialBailleurMissingNotification = async ({
  csEmail,
  residenceName,
  tenantName,
  tenantApartment,
}: {
  csEmail: string | string[];
  residenceName: string;
  tenantName: string;
  tenantApartment?: string;
}): Promise<boolean> => {
  const to = Array.isArray(csEmail) ? csEmail : csEmail.split(',').map(e => e.trim()).filter(Boolean);
  const tenantDisplay = tenantApartment ? `${tenantName} — Appt. ${tenantApartment}` : tenantName;

  const html = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;background-color:#f3f4f6;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#f3f4f6;">
    <tr><td align="center" style="padding:40px 20px;">
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:600px;background-color:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 6px rgba(0,0,0,0.1);">

        <!-- Header -->
        <tr>
          <td style="background:#ffffff;padding:28px 24px;text-align:center;border-bottom:1px solid #e5e7eb;">
            <img src="${LOGO_URL}" alt="O'Copro" style="height:44px;max-width:200px;margin-bottom:8px;border-radius:8px;" />
            <p style="margin:0;color:#6b7280;font-size:13px;">Gestion de copropriété</p>
          </td>
        </tr>

        <!-- Title -->
        <tr>
          <td style="padding:32px 24px 16px;">
            <span style="display:inline-block;background-color:#f59e0b;color:#ffffff;font-size:12px;font-weight:600;padding:4px 12px;border-radius:9999px;margin-bottom:12px;">⚠️ Action requise</span>
            <h2 style="margin:0 0 8px;color:#111827;font-size:20px;font-weight:700;">Bailleur social non renseigné</h2>
            <p style="margin:0;color:#6b7280;font-size:14px;">Un locataire de <strong>${residenceName}</strong> a indiqué être auprès d'un bailleur social.</p>
          </td>
        </tr>

        <!-- Resident -->
        <tr>
          <td style="padding:0 24px 16px;">
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#dbeafe;border-radius:12px;border:1px solid #93c5fd;">
              <tr><td style="padding:16px;">
                <p style="margin:0 0 6px;color:#1e40af;font-size:12px;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">🏠 Résident concerné</p>
                <p style="margin:0;color:#1e3a8a;font-size:15px;font-weight:700;">${tenantDisplay}</p>
                <p style="margin:4px 0 0;color:#3b82f6;font-size:13px;">${residenceName}</p>
              </td></tr>
            </table>
          </td>
        </tr>

        <!-- Message -->
        <tr>
          <td style="padding:0 24px 16px;">
            <p style="margin:0;color:#374151;font-size:14px;line-height:1.6;">
              Les coordonnées du bailleur social ne sont pas encore renseignées dans l'application.
              Sans ces informations, les demandes privatives de ce locataire ne peuvent pas être transmises au bailleur.
            </p>
          </td>
        </tr>

        <!-- Action -->
        <tr>
          <td style="padding:0 24px 16px;">
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#f0fdf4;border-radius:12px;border:1px solid #86efac;">
              <tr><td style="padding:16px;">
                <p style="margin:0 0 6px;color:#15803d;font-size:12px;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">✅ Action recommandée</p>
                <p style="margin:0;color:#14532d;font-size:14px;line-height:1.5;">
                  Renseignez les coordonnées du bailleur social dans l'application :<br/>
                  <strong>Paramètres résidence → Bailleur social</strong>
                </p>
              </td></tr>
            </table>
          </td>
        </tr>

        <!-- Note -->
        <tr>
          <td style="padding:0 24px 24px;">
            <p style="margin:0;color:#6b7280;font-size:13px;line-height:1.6;font-style:italic;">
              S'il n'y a pas de bailleur social dans la copropriété, vous pouvez le renseigner dans les paramètres de la résidence "Bailleurs sociaux" afin d'éviter que des locataires ne sélectionnent ce type par erreur.
            </p>
          </td>
        </tr>

        <!-- Footer -->
        <tr>
          <td style="background-color:#f9fafb;padding:20px 24px;border-top:1px solid #e5e7eb;text-align:center;">
            <p style="margin:0;color:#9ca3af;font-size:12px;">O'Copro — Gestion de copropriété</p>
          </td>
        </tr>

      </table>
    </td></tr>
  </table>
</body>
</html>`;

  return sendEmail({
    to,
    subject: `[O'Copro] Bailleur social non renseigné · ${residenceName}`,
    html,
  });
};

export default {
  sendEmail,
  sendRequestToSyndicEmail,
  sendRequestToBailleurEmail,
  sendRequestValidatedEmail,
  sendRequestTransmittedEmail,
  sendRequestRejectedEmail,
  sendRequestClosedEmail,
  sendNewRequestToCSEmail,
  sendNewResidenceNotificationEmail,
  sendNewMemberPendingNotification,
  sendMemberApprovedEmail,
  sendMemberRejectedEmail,
  sendSocialBailleurMissingNotification,
};
